<template>
  <div class="position-tracker-floating">
    <!-- Permission Dialog Modal -->
    <Teleport to="body">
      <Transition name="modal-fade">
        <div v-if="showPermissionDialog" class="permission-modal-overlay">
          <div class="permission-modal">
            <div class="modal-icon">
              <svg viewBox="0 0 24 24" width="48" height="48">
                <path
                  d="M12,2A8,8 0 0,1 20,10C20,14.4 12,22 12,22C12,22 4,14.4 4,10A8,8 0 0,1 12,2M12,4A6,6 0 0,0 6,10C6,12 12,18.7 12,18.7C12,18.7 18,12 18,10A6,6 0 0,0 12,4M12,7A3,3 0 0,1 15,10A3,3 0 0,1 12,13A3,3 0 0,1 9,10A3,3 0 0,1 12,7Z"
                  fill="#667eea"
                />
              </svg>
            </div>
            <h2 class="modal-title">{{ currentStep.title }}</h2>
            <p class="modal-description">{{ currentStep.description }}</p>
            <div v-if="currentStep.benefits" class="benefits-list">
              <div v-for="benefit in currentStep.benefits" :key="benefit" class="benefit-item">
                <svg class="benefit-icon" viewBox="0 0 20 20" width="20" height="20">
                  <path d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" fill="#10b981" />
                </svg>
                <span>{{ benefit }}</span>
              </div>
            </div>
            <div class="modal-actions">
              <button v-if="!currentStep.required" @click="skipPermission" class="btn-secondary">
                {{ currentStep.skipText || 'Skip' }}
              </button>
              <button @click="handlePermissionAction" class="btn-primary" :disabled="isProcessingPermission">
                {{ isProcessingPermission ? 'Processing...' : currentStep.actionText }}
              </button>
            </div>
            <div v-if="permissionError" class="permission-error">
              <p>{{ permissionError }}</p>
              <div class="error-actions">
                <button @click="openSettings" class="settings-link">Open Settings</button>
              </div>
            </div>
          </div>
        </div>
      </Transition>
    </Teleport>
    <!-- Floating action button -->
    <button class="tracker-fab" :class="fabClasses" :title="buttonTitle" @click="handleFabClick" :disabled="isWebPlatform">
      <Transition name="icon-rotate" mode="out-in">
        <svg v-if="isWebPlatform" class="marker-icon" viewBox="0 0 24 24" width="24" height="24">
          <path
            d="M12,2A8,8 0 0,1 20,10C20,14.4 12,22 12,22C12,22 4,14.4 4,10A8,8 0 0,1 12,2M12,4A6,6 0 0,0 6,10C6,12 12,18.7 12,18.7C12,18.7 18,12 18,10A6,6 0 0,0 12,4M15,7H9V9H15V7Z"
            fill="currentColor"
          />
        </svg>
        <svg v-else-if="running" class="marker-icon" viewBox="0 0 24 24" width="24" height="24">
          <path
            d="M12,2C15.31,2 18,4.66 18,7.95C18,12.41 12,19 12,19C12,19 6,12.41 6,7.95C6,4.66 8.69,2 12,2M12,6A2,2 0 0,0 10,8A2,2 0 0,0 12,10A2,2 0 0,0 14,8A2,2 0 0,0 12,6"
            fill="currentColor"
          />
        </svg>
        <svg v-else-if="permBlocked" class="marker-icon" viewBox="0 0 24 24" width="24" height="24">
          <path
            d="M12,2A8,8 0 0,1 20,10C20,14.4 12,22 12,22C12,22 4,14.4 4,10A8,8 0 0,1 12,2M12,4A6,6 0 0,0 6,10C6,12 12,18.7 12,18.7C12,18.7 18,12 18,10A6,6 0 0,0 12,4M11,15H13V17H11V15M11,7H13V13H11V7Z"
            fill="currentColor"
          />
        </svg>
        <svg v-else class="marker-icon" viewBox="0 0 24 24" width="24" height="24">
          <path
            d="M12,2A8,8 0 0,1 20,10C20,14.4 12,22 12,22C12,22 4,14.4 4,10A8,8 0 0,1 12,2M12,4A6,6 0 0,0 6,10C6,12 12,18.7 12,18.7C12,18.7 18,12 18,10A6,6 0 0,0 12,4M12,7A3,3 0 0,1 15,10A3,3 0 0,1 12,13A3,3 0 0,1 9,10A3,3 0 0,1 12,7Z"
            fill="currentColor"
          />
        </svg>
      </Transition>
      <div class="ripple-container">
        <div v-if="running" class="ripple"></div>
      </div>
    </button>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onBeforeUnmount, nextTick } from "vue"
import { registerPlugin, Capacitor } from "@capacitor/core"
import { Geolocation } from "@capacitor/geolocation"
import { LocalNotifications } from "@capacitor/local-notifications"
import { useOAuthService } from "@/services/OAuthService"
import AnalyticsController from "@/controllers/AnalyticsController"

const BackgroundGeolocation = registerPlugin("BackgroundGeolocation")

// Register native CoreMotion plugin
const CoreMotion = registerPlugin("CoreMotionPlugin")

const { getUserProfile } = useOAuthService()

// Configuration
const CONFIG = {
  useBatch: true,
  requestBackground: true,
  flushMs: 2500,
  maxBatch: 12,
  minDistM: 0.1,
  maxAccM: 120,
  teleportM: 60,
  accuracyThresholds: {
    initializing: 8,
    stabilizing: 10,
    locked: 15,
    moving: 25
  },
  lockCriteria: {
    minConfidence: 85,      // LOWERED from 95
    maxVariance: 3,         // INCREASED from 2
    minStationaryMs: 15000, // REDUCED from 30000 (15 seconds)
    minPoints: 15,
    maxClusterRadius: 5     // INCREASED from 3
  },
  motionThresholds: {
    stillSamples: 30,       // REDUCED from 80 (3 seconds instead of 8)
    movingSamples: 15,      // REDUCED from 25
    transitionCooldownMs: 3000 // REDUCED from 5000
  }
}

// Kalman Filter
class Kalman1D {
  constructor(q, r, p, x) {
    this.Q = q
    this.R = r
    this.P = p
    this.x = x
    this.lastUpdate = Date.now()
  }

  update(z, r = this.R, q = this.Q) {
    this.Q = q
    this.R = r
    this.P += this.Q
    const k = this.P / (this.P + this.R)
    this.x += k * (z - this.x)
    this.P *= (1 - k)
    this.lastUpdate = Date.now()
    return this.x
  }

  reset(x, p = 100) {
    this.x = x
    this.P = p
    this.lastUpdate = Date.now()
  }
}

const running = ref(false)
const permBlocked = ref(false)
const isWebPlatform = ref(false)
const showPermissionDialog = ref(false)
const currentPermissionStep = ref(0)
const isProcessingPermission = ref(false)
const permissionError = ref("")
const uploadQueue = ref([])
const flushTimer = ref(null)
const backoffDelay = ref(3000)
const permissionSteps = ref([])
const hasInitialCheck = ref(false)

const watcherId = ref(null)
const motionListener = ref(null)
const motionPermissionGranted = ref(false)

const isMoving = computed(() => motionState.value === 'moving')
const motionState = ref('uncertain')
const accelBuffer = ref([])
const stillCount = ref(0)
const moveCount = ref(0)
const stationaryStart = ref(0)
const lastMotionChange = ref(0)

const positionBuffer = ref([])
const clusterCenter = ref(null)
const clusterVariance = ref(0)
const clusterRadius = ref(0)
const confidence = ref(0)
const currentState = ref('initializing')
const heartbeatTimer = ref(null)
const currentPosition = ref(null)
const startTime = ref(0)
const lastUpload = ref(0)
const isLocked = ref(false)

const kalmanLat = ref(null)
const kalmanLon = ref(null)
const kalmanAlt = ref(null)

let lastLat = null
let lastLon = null
let lastTs = null
let totalReceived = 0
let totalSent = 0
let rejectedCount = 0

const IOS_PERMISSION_STEPS = [
  {
    id: "core",
    title: "Location Permission",
    description: "Grant access to device location for position tracking",
    method: "requestCore",
    required: true,
    actionText: "Grant Location",
    benefits: ["GPS positioning", "Network location", "Essential for tracking"]
  },
  {
    id: "background",
    title: "Background Location",
    description: "Continue tracking when app is minimized",
    method: "requestBackground",
    required: true,
    actionText: "Allow Background",
    benefits: ["Continuous tracking", "Background operation", "Complete history"]
  },
  {
    id: "notifications",
    title: "Enable Notifications",
    description: "Get updates about tracking status and accuracy",
    method: "requestNotifications",
    required: false,
    actionText: "Enable Notifications",
    skipText: "Skip",
    benefits: ["Status updates", "Position alerts", "Monitor accuracy"]
  }
]

const currentStep = computed(() => {
  return permissionSteps.value[currentPermissionStep.value] || permissionSteps.value[0]
})

const buttonTitle = computed(() => {
  if (isWebPlatform.value) return "Not available on web"
  if (running.value) return "Stop tracking"
  if (permBlocked.value) return "Fix permissions"
  return "Start tracking"
})

const fabClasses = computed(() => ({
  "is-tracking": running.value,
  "has-error": permBlocked.value,
  "is-disabled": isWebPlatform.value
}))

async function initializeConfig() {
  try {
    await getUserProfile()
    console.log("✓ Config initialized")
  } catch (e) {
    console.error("✗ Config init failed:", e)
  }
}

function detectPlatform() {
  isWebPlatform.value = !Capacitor.isNativePlatform()
  console.log(`Platform: ${isWebPlatform.value ? "Web" : "Native"}`)
}

async function checkAllPermissions() {
  try {
    const geolocationPerm = await Geolocation.checkPermissions()
    const notificationsPerm = await LocalNotifications.checkPermissions()
    
    return {
      location: geolocationPerm.location === "granted" || geolocationPerm.coarseLocation === "granted",
      background: true,
      notifications: notificationsPerm.display === "granted",
      locationEnabled: true
    }
  } catch (e) {
    console.error("Permission check failed:", e)
    return { location: false, background: false, notifications: false, locationEnabled: false }
  }
}

async function checkRequiredPermissions() {
  const status = await checkAllPermissions()
  const hasRequired = status.location && status.locationEnabled
  console.log(`Required permissions: ${hasRequired ? '✓' : '✗'}`)
  return hasRequired
}

async function buildPermissionSteps() {
  const status = await checkAllPermissions()
  const steps = []
  
  if (!status.location) {
    steps.push(IOS_PERMISSION_STEPS.find(s => s.id === "core"))
  }
  if (!status.background && CONFIG.requestBackground) {
    steps.push(IOS_PERMISSION_STEPS.find(s => s.id === "background"))
  }
  if (!status.notifications) {
    steps.push(IOS_PERMISSION_STEPS.find(s => s.id === "notifications"))
  }
  
  console.log(`Built ${steps.length} permission steps`)
  return steps.filter(s => s)
}

async function startPermissionFlow() {
  console.log("Starting permission flow...")
  permissionSteps.value = await buildPermissionSteps()
  
  if (permissionSteps.value.length === 0) {
    console.log("All permissions granted, starting tracker")
    await start()
    return
  }
  
  currentPermissionStep.value = 0
  permissionError.value = ""
  permBlocked.value = false
  showPermissionDialog.value = true
}

async function handlePermissionAction() {
  const step = currentStep.value
  if (!step) return
  
  console.log(`Requesting permission: ${step.id}`)
  isProcessingPermission.value = true
  permissionError.value = ""
  
  try {
    let granted = false
    
    switch (step.id) {
      case "core":
        await Geolocation.requestPermissions(["location"])
        granted = (await Geolocation.checkPermissions()).location === "granted"
        break
        
      case "background":
        granted = true
        break
        
      case "notifications":
        await LocalNotifications.requestPermissions()
        granted = (await LocalNotifications.checkPermissions()).display === "granted"
        break
    }
    
    console.log(`Permission ${step.id}: ${granted ? '✓' : '✗'}`)
    
    if (granted) {
      moveToNextStep()
    } else if (step.required) {
      permissionError.value = "Permission denied. Open settings to grant manually."
      permBlocked.value = true
    } else {
      moveToNextStep()
    }
  } catch (e) {
    console.error("Permission request failed:", e)
    if (step.required) {
      permissionError.value = "An error occurred. Please use settings."
      permBlocked.value = true
    } else {
      moveToNextStep()
    }
  } finally {
    isProcessingPermission.value = false
  }
}

function skipPermission() {
  const step = currentStep.value
  if (!step.required) {
    console.log(`Skipping: ${step.id}`)
    moveToNextStep()
  }
}

async function moveToNextStep() {
  currentPermissionStep.value++
  permissionError.value = ""
  
  if (currentPermissionStep.value >= permissionSteps.value.length) {
    console.log("Permission flow complete")
    showPermissionDialog.value = false
    const hasRequired = await checkRequiredPermissions()
    if (hasRequired) {
      await start()
    } else {
      permBlocked.value = true
    }
  }
}

function openSettings() {
  const step = currentStep.value
  console.log(`Opening settings for: ${step.id}`)
  try {
    BackgroundGeolocation.openSettings()
  } catch (e) {
    console.error("Failed to open settings:", e)
  }
}

async function start() {
  if (running.value || isWebPlatform.value) {
    console.log("Cannot start - already running or web platform")
    return
  }
  
  const hasRequired = await checkRequiredPermissions()
  if (!hasRequired) {
    console.log("Missing required permissions")
    await startPermissionFlow()
    return
  }
  
  try {
    console.log("🚀 Starting tracker...")
    
    // Reset all state
    startTime.value = Date.now()
    currentState.value = 'initializing'
    motionState.value = 'stationary'
    confidence.value = 0
    positionBuffer.value = []
    accelBuffer.value = []
    stillCount.value = 0
    moveCount.value = 0
    clusterCenter.value = null
    clusterVariance.value = 0
    clusterRadius.value = 0
    isLocked.value = false
    kalmanLat.value = null
    kalmanLon.value = null
    kalmanAlt.value = null
    lastUpload.value = 0
    stationaryStart.value = Date.now()
    lastMotionChange.value = Date.now()
    lastLat = null
    lastLon = null
    lastTs = null
    rejectedCount = 0

    // Start smart motion detection (only state changes, not continuous)
    try {
      const availability = await CoreMotion.isAvailable()
      if (availability.available) {
        await CoreMotion.startMotionDetection()
        
        // Listen for motion STATE CHANGES only (not continuous accelerometer data!)
        motionListener.value = await CoreMotion.addListener('motionStateChanged', (data) => {
          const now = Date.now()
          const timeSinceLastChange = now - lastMotionChange.value
          
          // Apply cooldown to prevent rapid state changes
          if (timeSinceLastChange < CONFIG.motionThresholds.transitionCooldownMs) {
            return
          }
          
          console.log(`📍 Motion: ${data.state.toUpperCase()} (confidence: ${data.confidence})`)
          
          if (data.state === 'stationary') {
            if (motionState.value !== 'stationary') {
              motionState.value = 'stationary'
              stationaryStart.value = now
              lastMotionChange.value = now
              console.log("📍 Motion: STATIONARY")
            }
          } else if (data.state === 'walking' || data.state === 'running' || data.state === 'automotive' || data.state === 'cycling') {
            if (motionState.value !== 'moving') {
              motionState.value = 'moving'
              lastMotionChange.value = now
              confidence.value = Math.max(0, confidence.value - 30)
              console.log("🏃 Motion: MOVING")
            }
          }
        })
        
        motionPermissionGranted.value = true
        console.log("✓ Native Motion Detection active (state change only)")
      } else {
        console.warn("⚠ Motion activity not available")
        motionPermissionGranted.value = false
      }
    } catch (e) {
      console.warn("⚠ Motion detection failed, using GPS-only mode:", e)
      motionPermissionGranted.value = false
    }

    // Start location tracking
    watcherId.value = await BackgroundGeolocation.addWatcher(
      {
        backgroundMessage: "Cancel to prevent battery drain.",
        backgroundTitle: "Tracking You.",
        requestPermissions: true,
        stale: false,
        distanceFilter: CONFIG.minDistM
      },
      (location, error) => {
        if (error) {
          console.error("Location error:", error)
          if (error.code === "NOT_AUTHORIZED") {
            permBlocked.value = true
          }
          return
        }
        totalReceived++
        processLocation(location)
      }
    )
    
    running.value = true
    permBlocked.value = false
    console.log("✓ Tracker started successfully")
    console.log(`   Motion: ${motionPermissionGranted.value ? 'Activity Detection' : 'GPS Speed Fallback'}`)
    
  } catch (e) {
    console.error("✗ Start failed:", e)
    permBlocked.value = true
  }
}
async function stop() {
  if (!running.value) return
  
  try {
    console.log("🛑 Stopping tracker...")
    
    // Stop location tracking
    if (watcherId.value) {
      await BackgroundGeolocation.removeWatcher({ id: watcherId.value })
      watcherId.value = null
      console.log("✓ Location tracking stopped")
    }
    
    // Stop motion listener
    if (motionListener.value) {
      motionListener.value.remove()
      motionListener.value = null
      console.log("✓ Motion listener removed")
    }
    
    // Stop motion detection
    if (motionPermissionGranted.value) {
      try {
        await CoreMotion.stopMotionDetection()
        console.log("✓ Motion detection stopped")
      } catch (e) {
        console.warn("⚠ Failed to stop motion detection:", e)
      }
    }
    
    // Stop heartbeat timer
    if (heartbeatTimer.value) {
      clearInterval(heartbeatTimer.value)
      heartbeatTimer.value = null
      console.log("✓ Heartbeat timer stopped")
    }
    
    // Stop flush timer
    if (flushTimer.value) {
      clearTimeout(flushTimer.value)
      flushTimer.value = null
      console.log("✓ Flush timer stopped")
    }
    
    // Flush remaining queue
    if (uploadQueue.value.length > 0) {
      console.log(`📤 Flushing ${uploadQueue.value.length} remaining points...`)
      await flushQueue()
    }
    
    running.value = false
    console.log("✓ Tracker stopped successfully")
    console.log(`📊 Session Stats:`)
    console.log(`   Received: ${totalReceived} positions`)
    console.log(`   Sent: ${totalSent} positions`)
    console.log(`   Rejected: ${rejectedCount} positions`)
    console.log(`   Success Rate: ${totalReceived > 0 ? ((totalSent / totalReceived) * 100).toFixed(1) : 0}%`)
    
  } catch (e) {
    console.error("✗ Stop failed:", e)
  }
}

async function handleFabClick() {
  if (isWebPlatform.value) return
  
  if (running.value) {
    await stop()
  } else {
    const hasRequired = await checkRequiredPermissions()
    if (!hasRequired) {
      await startPermissionFlow()
    } else {
      await start()
    }
  }
}

function distanceMeters(lat1, lon1, lat2, lon2) {
  const R = 6371000
  const dLat = ((lat2 - lat1) * Math.PI) / 180
  const dLon = ((lon2 - lon1) * Math.PI) / 180
  const a = Math.sin(dLat / 2) ** 2 + 
    Math.cos((lat1 * Math.PI) / 180) * Math.cos((lat2 * Math.PI) / 180) * 
    Math.sin(dLon / 2) ** 2
  return 2 * R * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))
}

function handleAccelEvent(event) {
  const { x, y, z } = event
  // CoreMotion gives values in G's already, no need to divide by 9.81
  const magnitude = Math.sqrt(x * x + y * y + z * z)
  
  accelBuffer.value.push(magnitude)
  if (accelBuffer.value.length > 30) accelBuffer.value.shift()
  
  if (accelBuffer.value.length < 10) return

  const avg = accelBuffer.value.reduce((a, b) => a + b, 0) / accelBuffer.value.length
  const varSum = accelBuffer.value.reduce((a, x) => a + (x - avg) ** 2, 0)
  const variance = varSum / accelBuffer.value.length

  const MOTION_THRESHOLD = 0.05
  const VARIANCE_THRESHOLD = 0.01
  const potentialStill = Math.abs(avg - 1) < MOTION_THRESHOLD && variance < VARIANCE_THRESHOLD

  if (potentialStill) {
    stillCount.value++
    moveCount.value = 0
  } else {
    moveCount.value++
    stillCount.value = 0
  }

  const now = Date.now()
  const timeSinceLastChange = now - lastMotionChange.value

  if (timeSinceLastChange < CONFIG.motionThresholds.transitionCooldownMs) {
    return
  }

  const uncertainThreshold = 15
  const moveThreshold = CONFIG.motionThresholds.movingSamples
  const stillThreshold = CONFIG.motionThresholds.stillSamples

  if (motionState.value === 'uncertain') {
    if (stillCount.value > uncertainThreshold) {
      motionState.value = 'stationary'
      lastMotionChange.value = now
      stationaryStart.value = now
      console.log("📍 Motion: STATIONARY")
    } else if (moveCount.value > uncertainThreshold) {
      motionState.value = 'moving'
      lastMotionChange.value = now
      console.log("🏃 Motion: MOVING")
    }
  } else if (motionState.value === 'stationary') {
    if (moveCount.value > moveThreshold) {
      motionState.value = 'moving'
      lastMotionChange.value = now
      confidence.value = Math.max(0, confidence.value - 30)
      console.log("🏃 Motion: STATIONARY → MOVING")
    }
  } else if (motionState.value === 'moving') {
    if (stillCount.value > stillThreshold) {
      motionState.value = 'stationary'
      lastMotionChange.value = now
      stationaryStart.value = now
      console.log("📍 Motion: MOVING → STATIONARY")
    }
  }
}

function getStateBasedAccuracyThreshold() {
  switch (currentState.value) {
    case 'initializing':
      return CONFIG.accuracyThresholds.initializing
    case 'stabilizing':
      return CONFIG.accuracyThresholds.stabilizing
    case 'locked':
      return CONFIG.accuracyThresholds.locked
    case 'moving':
      return CONFIG.accuracyThresholds.moving
    default:
      return CONFIG.accuracyThresholds.initializing
  }
}

function processLocation(location) {
  const ts = Date.now()
  const accuracy = location.accuracy
  
  const maxAccuracy = getStateBasedAccuracyThreshold()
  if (accuracy > maxAccuracy) {
    rejectedCount++
    console.log(`❌ Rejected: accuracy ${accuracy.toFixed(1)}m > ${maxAccuracy}m (state: ${currentState.value})`)
    return
  }

  // GPS fallback when CoreMotion unavailable
  if (!motionPermissionGranted.value) {
    const speed = location.speed || 0
    const now = Date.now()
    const timeSinceLastChange = now - lastMotionChange.value
    
    if (timeSinceLastChange > 5000) {
      if (speed < 0.3) {
        if (motionState.value !== 'stationary') {
          motionState.value = 'stationary'
          stationaryStart.value = now
          lastMotionChange.value = now
          console.log("📍 Motion (GPS): STATIONARY")
        }
      } else if (speed > 0.5) {
        if (motionState.value !== 'moving') {
          motionState.value = 'moving'
          lastMotionChange.value = now
          console.log("🏃 Motion (GPS): MOVING")
        }
      }
    }
  }

  if (motionState.value === 'moving' && currentState.value !== 'initializing') {
    console.log(`🏃 Moving: skipping position`)
    return
  }

  if (!kalmanLat.value) {
    kalmanLat.value = new Kalman1D(0.0001, accuracy ** 2, 100, location.latitude)
    kalmanLon.value = new Kalman1D(0.0001, accuracy ** 2, 100, location.longitude)
    if (location.altitude !== null && location.altitude !== undefined) {
      kalmanAlt.value = new Kalman1D(0.0001, (location.altitudeAccuracy || 5) ** 2, 100, location.altitude)
    }
  }

  const q = isMoving.value ? 0.5 : 0.0001
  const filteredLat = kalmanLat.value.update(location.latitude, accuracy ** 2, q)
  const filteredLon = kalmanLon.value.update(location.longitude, accuracy ** 2, q)
  
  let filteredAlt = location.altitude
  if (kalmanAlt.value && location.altitude !== null) {
    filteredAlt = kalmanAlt.value.update(
      location.altitude, 
      (location.altitudeAccuracy || 5) ** 2, 
      q
    )
  }

  if (lastLat !== null && lastLon !== null) {
    const rawDist = distanceMeters(location.latitude, location.longitude, lastLat, lastLon)
    const filteredDist = distanceMeters(filteredLat, filteredLon, lastLat, lastLon)
    
    if (filteredDist > 50 && filteredDist > rawDist * 2) {
      rejectedCount++
      console.log(`❌ Rejected: Kalman jump ${filteredDist.toFixed(1)}m`)
      kalmanLat.value.reset(location.latitude)
      kalmanLon.value.reset(location.longitude)
      return
    }
  }

  const filteredPos = {
    ts,
    lat: filteredLat,
    lon: filteredLon,
    accuracy,
    speed: location.speed || 0,
    altitude: filteredAlt,
    heading: location.bearing || null,
    source: "background-geolocation"
  }

  if (clusterCenter.value && currentState.value === 'locked') {
    const dist = distanceMeters(
      filteredPos.lat, 
      filteredPos.lon, 
      clusterCenter.value.lat, 
      clusterCenter.value.lon
    )
    if (dist > 10) {
      rejectedCount++
      console.log(`❌ Rejected: too far from locked cluster (${dist.toFixed(1)}m)`)
      return
    }
  }

  if (lastLat !== null && lastLon !== null && !isMoving.value && currentState.value !== 'initializing') {
    const dist = distanceMeters(filteredPos.lat, filteredPos.lon, lastLat, lastLon)
    const timeDiff = (ts - lastTs) / 1000
    if (timeDiff > 0) {
      const speed = dist / timeDiff
      if (speed > 3) {
        rejectedCount++
        console.log(`❌ Rejected: speed ${speed.toFixed(1)} m/s while stationary`)
        return
      }
    }
  }

  lastLat = filteredPos.lat
  lastLon = filteredPos.lon
  lastTs = ts

  positionBuffer.value.push(filteredPos)
  if (positionBuffer.value.length > 50) positionBuffer.value.shift()
  console.log(`✓ Position buffered (${positionBuffer.value.length}/50) - State: ${currentState.value}`)

  updateOverallState()
}

function computeCluster() {
  const bufferSize = positionBuffer.value.length
  
  if (bufferSize < CONFIG.lockCriteria.minPoints) {
    console.log(`⚠ Cluster: insufficient points (${bufferSize}/${CONFIG.lockCriteria.minPoints})`)
    return
  }

  const now = Date.now()
  const tau = 60000
  let sumLat = 0, sumLon = 0, sumW = 0, sumAlt = 0
  const weights = []
  let avgAcc = 0

  positionBuffer.value.forEach(pos => {
    const timeDecay = Math.exp(-(now - pos.ts) / tau)
    const w = timeDecay / (pos.accuracy ** 2 + 1)
    sumLat += pos.lat * w
    sumLon += pos.lon * w
    if (pos.altitude !== null) sumAlt += pos.altitude * w
    sumW += w
    weights.push(w)
    avgAcc += pos.accuracy
  })

  if (sumW === 0) return
  
  const centLat = sumLat / sumW
  const centLon = sumLon / sumW
  const centAlt = sumAlt / sumW || null
  avgAcc /= bufferSize

  let sumDistSq = 0, sumW2 = 0, count = 0
  let maxDist = 0
  const outlierThreshold = Math.min(avgAcc, 5)

  positionBuffer.value.forEach((pos, i) => {
    const dist = distanceMeters(pos.lat, pos.lon, centLat, centLon)
    
    if (dist > outlierThreshold) {
      console.log(`⚠ Outlier rejected: ${dist.toFixed(2)}m > ${outlierThreshold.toFixed(2)}m`)
      return
    }
    
    sumDistSq += dist ** 2 * weights[i]
    sumW2 += weights[i]
    count++
    maxDist = Math.max(maxDist, dist)
  })

  if (count < CONFIG.lockCriteria.minPoints) {
    console.log(`⚠ Cluster: too many outliers, only ${count} valid points`)
    return
  }

  const variance = sumW2 > 0 ? sumDistSq / sumW2 : 999
  clusterVariance.value = variance
  clusterRadius.value = maxDist
  clusterCenter.value = { lat: centLat, lon: centLon, altitude: centAlt }
  
  console.log(`📊 Cluster: var=${variance.toFixed(2)}, radius=${maxDist.toFixed(2)}m, points=${count}`)
}

function validateClusterStability() {
  if (!clusterCenter.value) return false
  
  const isStable = 
    clusterVariance.value < CONFIG.lockCriteria.maxVariance &&
    clusterRadius.value < CONFIG.lockCriteria.maxClusterRadius &&
    positionBuffer.value.length >= CONFIG.lockCriteria.minPoints
  
  return isStable
}

function isClusterReliable() {
  if (!clusterCenter.value) return false
  
  const now = Date.now()
  const timeSinceStationary = now - stationaryStart.value
  
  return validateClusterStability() && 
         timeSinceStationary >= CONFIG.lockCriteria.minStationaryMs &&
         motionState.value === 'stationary'
}

function updateConfidence() {
  const varScore = Math.max(0, 100 - Math.sqrt(clusterVariance.value) * 25)
  const countScore = Math.min(100, (positionBuffer.value.length / CONFIG.lockCriteria.minPoints) * 100)
  const timeStationary = Date.now() - stationaryStart.value
  const timeScore = Math.min(100, (timeStationary / CONFIG.lockCriteria.minStationaryMs) * 100)
  const radiusScore = Math.max(0, 100 - (clusterRadius.value / CONFIG.lockCriteria.maxClusterRadius) * 100)
  const motionScore = motionState.value === 'stationary' ? 100 : 0
  
  confidence.value = (
    varScore * 0.3 + 
    countScore * 0.2 + 
    timeScore * 0.2 + 
    radiusScore * 0.2 +
    motionScore * 0.1
  )
  
  confidence.value = Math.max(0, Math.min(100, confidence.value))
}

function updateOverallState() {
  computeCluster()
  updateConfidence()
  
  const now = Date.now()

  switch (currentState.value) {
    case 'initializing':
      if (positionBuffer.value.length >= 10 || now - startTime.value > 30000) {
        if (motionState.value === 'stationary') {
          currentState.value = 'stabilizing'
          console.log("🔄 State: INITIALIZING → STABILIZING")
        }
      }
      break

    case 'stabilizing':
      if (motionState.value !== 'stationary') {
        currentState.value = 'moving'
        positionBuffer.value = []
        confidence.value = 0
        isLocked.value = false
        console.log("🔄 State: STABILIZING → MOVING")
      } else if (
        confidence.value >= CONFIG.lockCriteria.minConfidence &&
        isClusterReliable()
      ) {
        currentState.value = 'locked'
        isLocked.value = true
        lastUpload.value = now
        console.log("🔒 State: STABILIZING → LOCKED")
        console.log(`   Confidence: ${confidence.value.toFixed(1)}%, Variance: ${clusterVariance.value.toFixed(2)}, Radius: ${clusterRadius.value.toFixed(2)}m`)
        
        const point = createPointFromCluster(now)
        if (shouldEmitPosition(point)) {
          enqueuePoint(point)
        }
        
        if (!heartbeatTimer.value) {
          heartbeatTimer.value = setInterval(handleHeartbeat, 30000)
        }
      }
      break

    case 'locked':
      if (motionState.value !== 'stationary') {
        currentState.value = 'moving'
        isLocked.value = false
        confidence.value = 0
        positionBuffer.value = []
        if (heartbeatTimer.value) {
          clearInterval(heartbeatTimer.value)
          heartbeatTimer.value = null
        }
        console.log("🔄 State: LOCKED → MOVING")
      } else if (!isClusterReliable()) {
        currentState.value = 'stabilizing'
        isLocked.value = false
        if (heartbeatTimer.value) {
          clearInterval(heartbeatTimer.value)
          heartbeatTimer.value = null
        }
        console.log("🔄 State: LOCKED → STABILIZING (cluster unreliable)")
      }
      break

    case 'moving':
      if (motionState.value === 'stationary' && now - lastMotionChange.value > 10000) {
        currentState.value = 'stabilizing'
        positionBuffer.value = []
        confidence.value = 0
        console.log("🔄 State: MOVING → STABILIZING")
      }
      break
  }
}

function handleHeartbeat() {
  if (currentState.value === 'locked' && isClusterReliable()) {
    const now = Date.now()
    const point = createPointFromCluster(now)
    if (shouldEmitPosition(point)) {
      enqueuePoint(point)
      lastUpload.value = now
      console.log("💓 Heartbeat: position emitted")
    }
  }
}

function createPointFromCluster(ts = Date.now()) {
  if (!clusterCenter.value) return null
  
  return {
    ts,
    lat: clusterCenter.value.lat,
    lon: clusterCenter.value.lon,
    accuracy: Math.max(5, Math.sqrt(clusterVariance.value) + 5),
    speed: 0,
    altitude: clusterCenter.value.altitude,
    heading: null,
    isMoving: isMoving.value,
    isLocked: isLocked.value,
    source: "background-geolocation-processed",
    motionConfidence: confidence.value,
    floorIndex: 0,
    isStationary: motionState.value === 'stationary',
    meta: { app: "PIA", source: "vue-npm-v1", version: "2.0" }
  }
}

function shouldEmitPosition(point) {
  if (!point || !point.lat || !point.lon) {
    console.log("❌ Emit blocked: invalid point")
    return false
  }

  if (currentState.value !== 'locked') {
    console.log(`❌ Emit blocked: state is ${currentState.value}, not locked`)
    return false
  }

  if (confidence.value < 90) {
    console.log(`❌ Emit blocked: confidence ${confidence.value.toFixed(1)}% < 90%`)
    return false
  }

  if (motionState.value === 'moving') {
    console.log("❌ Emit blocked: currently moving")
    return false
  }

  if (!isClusterReliable()) {
    console.log("❌ Emit blocked: cluster not reliable")
    return false
  }

  if (point.accuracy > CONFIG.maxAccM) {
    console.log(`❌ Emit blocked: accuracy ${point.accuracy.toFixed(1)}m too high`)
    return false
  }

  if (lastLat && lastLon) {
    const dist = distanceMeters(lastLat, lastLon, point.lat, point.lon)
    const minDist = point.accuracy < 10 ? CONFIG.minDistM * 0.5 : CONFIG.minDistM
    
    if (dist < minDist) {
      console.log(`❌ Emit blocked: distance ${dist.toFixed(2)}m < ${minDist.toFixed(2)}m`)
      return false
    }
    
    if (dist > CONFIG.teleportM && point.accuracy > 20) {
      console.log(`❌ Emit blocked: teleport detected ${dist.toFixed(2)}m`)
      return false
    }
  }

  return true
}

function enqueuePoint(point) {
  if (!point) return
  
  console.log(`✅ EMIT #${totalSent + 1}: lat=${point.lat.toFixed(6)}, lon=${point.lon.toFixed(6)}, acc=${point.accuracy.toFixed(1)}m, conf=${confidence.value.toFixed(1)}%`)
  
  lastLat = point.lat
  lastLon = point.lon
  
  const queueItem = {
    ...point,
    meta: { app: "PIA", source: "vue-npm-v1", version: "2.0" }
  }
  
  uploadQueue.value.push(queueItem)
  console.log(`Queue: ${uploadQueue.value.length}/${CONFIG.maxBatch}`)
  
  if (uploadQueue.value.length >= CONFIG.maxBatch) {
    console.log("Queue full, flushing immediately")
    flushQueue()
  } else {
    ensureFlushTimer()
  }
}

function ensureFlushTimer() {
  if (flushTimer.value) return
  
  flushTimer.value = setTimeout(() => {
    flushTimer.value = null
    flushQueue()
  }, CONFIG.flushMs)
}

async function flushQueue() {
  if (!uploadQueue.value.length) return
  
  const batch = uploadQueue.value.splice(0, CONFIG.maxBatch)
  totalSent += batch.length
  console.log(`📤 Uploading ${batch.length} points (total: ${totalSent})`)
  
  try {
    const normalized = batch.map(b => ({
      ts: b.ts,
      lat: b.lat,
      lon: b.lon,
      accuracy: b.accuracy,
      speed: b.speed,
      altitude: b.altitude,
      heading: b.heading,
      is_moving: b.isMoving
    }))
    
    const result = await AnalyticsController.postLocationsBatch(normalized)
    
    if (!result?.ok) {
      throw new Error("batch upload failed")
    }
    
    console.log("✓ Upload successful")
    backoffDelay.value = 3000
    
    if (uploadQueue.value.length > 0) {
      console.log(`${uploadQueue.value.length} points remaining`)
      ensureFlushTimer()
    }
  } catch (e) {
    console.error("✗ Upload failed:", e)
    uploadQueue.value.unshift(...batch)
    console.log(`Retrying in ${backoffDelay.value}ms`)
    setTimeout(ensureFlushTimer, backoffDelay.value)
    backoffDelay.value = Math.min(backoffDelay.value * 2, 60000)
  }
}

async function checkAndAutoStart() {
  await nextTick()
  await new Promise(resolve => setTimeout(resolve, 500))
  
  console.log("Checking for auto-start...")
  const hasRequired = await checkRequiredPermissions()
  
  if (hasRequired) {
    console.log("Auto-starting tracker")
    await start()
  } else {
    console.log("Missing permissions, cannot auto-start")
  }
}

onMounted(async () => {
  console.log("Component mounted")
  await initializeConfig()
  detectPlatform()
  
  if (isWebPlatform.value) {
    console.log("Web platform detected, exiting")
    return
  }
  
  try {
    if (!hasInitialCheck.value) {
      hasInitialCheck.value = true
      console.log("Starting initial permission check and auto-start")
      await checkAndAutoStart()
    }
  } catch (e) {
    console.error("Init failed:", e)
  }
})

onBeforeUnmount(() => {
  console.log("Component unmounting")
  try {
    stop()
    if (flushTimer.value) {
      clearTimeout(flushTimer.value)
    }
    if (uploadQueue.value.length > 0) {
      console.log(`Flushing ${uploadQueue.value.length} remaining points`)
      flushQueue()
    }
  } catch (e) {
    console.error("Cleanup error:", e)
  }
})
</script>
<style scoped>
.position-tracker-floating {
  position: fixed;
  right: 65px;
  bottom: calc(var(--app-safe-bottom) + var(--app-bottom-nav-height) + 180px);
  z-index: 9998;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 12px;
  pointer-events: none;
}

.permission-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.6);
  backdrop-filter: blur(10px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000;
  padding: 20px;
  pointer-events: auto;
}

.permission-modal {
  background: white;
  border-radius: 24px;
  padding: 32px;
  max-width: 440px;
  width: 100%;
  box-shadow: 0 24px 48px rgba(0, 0, 0, 0.12), 0 12px 24px rgba(0, 0, 0, 0.08);
  animation: modalSlideIn 0.3s ease-out;
}

@keyframes modalSlideIn {
  from {
    opacity: 0;
    transform: translateY(20px) scale(0.95);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

.modal-icon {
  display: flex;
  justify-content: center;
  margin-bottom: 20px;
}

.modal-title {
  font-size: 24px;
  font-weight: 700;
  color: #1f2937;
  margin: 0 0 12px 0;
  text-align: center;
}

.modal-description {
  font-size: 15px;
  line-height: 1.6;
  color: #6b7280;
  margin: 0 0 24px 0;
  text-align: center;
}

.benefits-list {
  background: #f9fafb;
  border-radius: 12px;
  padding: 16px;
  margin-bottom: 24px;
}

.benefit-item {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  margin-bottom: 12px;
}

.benefit-item:last-child {
  margin-bottom: 0;
}

.benefit-icon {
  flex-shrink: 0;
  margin-top: 2px;
}

.benefit-item span {
  font-size: 14px;
  line-height: 1.5;
  color: #4b5563;
}

.modal-actions {
  display: flex;
  gap: 12px;
  justify-content: center;
}

.btn-primary {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border: none;
  border-radius: 12px;
  padding: 14px 28px;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  flex: 1;
  max-width: 240px;
}

.btn-primary:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 8px 20px rgba(102, 126, 234, 0.3);
}

.btn-primary:disabled {
  opacity: 0.6;
  cursor: not-allowed;
  transform: none;
}

.btn-secondary {
  background: transparent;
  color: #6b7280;
  border: 2px solid #e5e7eb;
  border-radius: 12px;
  padding: 14px 28px;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
}

.btn-secondary:hover {
  background: #f9fafb;
  border-color: #d1d5db;
}

.permission-error {
  margin-top: 20px;
  padding: 16px;
  background: #fef2f2;
  border: 1px solid #fecaca;
  border-radius: 12px;
  text-align: center;
}

.permission-error p {
  color: #991b1b;
  font-size: 14px;
  margin: 0 0 12px 0;
}

.error-actions {
  display: flex;
  gap: 8px;
  justify-content: center;
}

.settings-link {
  background: none;
  border: none;
  color: #3b82f6;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  padding: 6px 12px;
  text-decoration: underline;
  transition: color 0.2s ease;
  border-radius: 6px;
}

.settings-link:hover {
  color: #2563eb;
  background: rgba(59, 130, 246, 0.1);
}

.tracker-fab {
  pointer-events: auto;
  position: relative;
  width: 64px;
  height: 64px;
  border-radius: 50%;
  border: none;
  outline: none;
  cursor: pointer;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 12px 40px rgba(102, 126, 234, 0.25), 0 4px 16px rgba(0, 0, 0, 0.08), inset 0 1px 0 rgba(255, 255, 255, 0.1);
  transition: all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275);
  overflow: hidden;
}

.tracker-fab::before {
  content: "";
  position: absolute;
  inset: 0;
  border-radius: 50%;
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.2) 0%, rgba(255, 255, 255, 0.05) 100%);
  opacity: 0;
  transition: opacity 0.3s ease;
}

.tracker-fab:hover:not(.is-disabled) {
  transform: translateY(-4px) scale(1.08);
  box-shadow: 0 20px 50px rgba(102, 126, 234, 0.35), 0 8px 24px rgba(0, 0, 0, 0.12), inset 0 1px 0 rgba(255, 255, 255, 0.15);
}

.tracker-fab:hover:not(.is-disabled)::before {
  opacity: 1;
}

.tracker-fab:active:not(.is-disabled) {
  transform: translateY(-2px) scale(1.02);
  transition: all 0.15s ease;
}

.tracker-fab.is-tracking {
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  box-shadow: 0 12px 40px rgba(16, 185, 129, 0.25), 0 4px 16px rgba(0, 0, 0, 0.08), inset 0 1px 0 rgba(255, 255, 255, 0.1);
}

.tracker-fab.is-tracking:hover {
  box-shadow: 0 20px 50px rgba(16, 185, 129, 0.35), 0 8px 24px rgba(0, 0, 0, 0.12), inset 0 1px 0 rgba(255, 255, 255, 0.15);
}

.tracker-fab.has-error {
  background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
  box-shadow: 0 12px 40px rgba(239, 68, 68, 0.25), 0 4px 16px rgba(0, 0, 0, 0.08), inset 0 1px 0 rgba(255, 255, 255, 0.1);
}

.tracker-fab.has-error:hover:not(.is-disabled) {
  box-shadow: 0 20px 50px rgba(239, 68, 68, 0.35), 0 8px 24px rgba(0, 0, 0, 0.12), inset 0 1px 0 rgba(255, 255, 255, 0.15);
}

.tracker-fab.is-disabled {
  opacity: 0.5;
  cursor: not-allowed;
  transform: none !important;
  background: linear-gradient(135deg, #9ca3af 0%, #6b7280 100%);
}

.marker-icon {
  transition: all 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275);
  filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.1));
  color: #ffffff;
}

.ripple-container {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  border-radius: 50%;
  overflow: hidden;
  pointer-events: none;
}

.ripple {
  position: absolute;
  top: 50%;
  left: 50%;
  width: 0;
  height: 0;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.3);
  transform: translate(-50%, -50%);
  animation: ripple-animation 2s infinite;
}

@keyframes ripple-animation {
  0% {
    width: 0;
    height: 0;
    opacity: 1;
  }
  100% {
    width: 140px;
    height: 140px;
    opacity: 0;
  }
}

.modal-fade-enter-active,
.modal-fade-leave-active {
  transition: all 0.3s ease;
}

.modal-fade-enter-from,
.modal-fade-leave-to {
  opacity: 0;
}

.icon-rotate-enter-active,
.icon-rotate-leave-active {
  transition: all 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275);
}

.icon-rotate-enter-from {
  opacity: 0;
  transform: rotate(180deg) scale(0.6);
}

.icon-rotate-leave-to {
  opacity: 0;
  transform: rotate(-180deg) scale(0.6);
}

@media (max-width: 960px) {
  .position-tracker-floating {
    right: 35px;
    bottom: calc(var(--app-safe-bottom) + var(--app-bottom-nav-height) + 180px);
  }
  .tracker-fab {
    width: 60px;
    height: 60px;
  }
  .marker-icon {
    width: 22px;
    height: 22px;
  }
  .permission-modal {
    padding: 24px;
    max-width: 380px;
  }
}

@media (max-width: 768px) {
  .position-tracker-floating {
    right: 25px;
    bottom: calc(var(--app-safe-bottom) + var(--app-bottom-nav-height) + 160px);
  }
  .tracker-fab {
    width: 56px;
    height: 56px;
  }
  .marker-icon {
    width: 20px;
    height: 20px;
  }
  .permission-modal {
    padding: 20px;
    max-width: calc(100% - 40px);
  }
  .modal-actions {
    flex-direction: column;
  }
  .btn-primary,
  .btn-secondary {
    width: 100%;
    max-width: none;
  }
}

@media (max-width: 480px) {
  .position-tracker-floating {
    right: 20px;
    bottom: calc(var(--app-safe-bottom) + var(--app-bottom-nav-height) + 140px);
  }
  .tracker-fab {
    width: 52px;
    height: 52px;
  }
  .marker-icon {
    width: 18px;
    height: 18px;
  }
  .permission-modal {
    padding: 20px;
    border-radius: 20px;
  }
  .modal-title {
    font-size: 20px;
  }
  .modal-description {
    font-size: 14px;
  }
}

@media (prefers-color-scheme: dark) {
  .permission-modal {
    background: #1f2937;
    color: #e5e7eb;
  }
  .modal-title {
    color: #f3f4f6;
  }
  .modal-description {
    color: #d1d5db;
  }
  .benefits-list {
    background: #111827;
  }
  .benefit-item span {
    color: #9ca3af;
  }
  .btn-secondary {
    color: #d1d5db;
    border-color: #374151;
  }
  .btn-secondary:hover {
    background: #1f2937;
    border-color: #4b5563;
  }
}

@media (prefers-reduced-motion: reduce) {
  .tracker-fab,
  .modal-fade-enter-active,
  .modal-fade-leave-active,
  .icon-rotate-enter-active,
  .icon-rotate-leave-active {
    animation: none !important;
    transition: none !important;
  }
  .ripple {
    display: none;
  }
}

@media (prefers-contrast: high) {
  .tracker-fab,
  .permission-modal,
  .btn-primary,
  .btn-secondary {
    border: 2px solid currentColor;
  }
}
</style>
